import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Flatpickr from "react-flatpickr";
import Popup from "reactjs-popup";
import { Button, Plus } from "@opendate-app/setlist";
import { offsetDateForServer } from "../../../shared/timeZoneLogic";
import { useSeriesShowContext } from "../SeriesShowApp";
import { fmtPrice, fmtCurrencySymbol } from "../passFormatting";
import useCheckoutQuestions from "../useCheckoutQuestions";
import CheckoutQuestionsEditor from "./CheckoutQuestionsEditor";
import PassFeesSection from "./PassFeesSection";
import InventoryPoolSelect from "./InventoryPoolSelect";
import GeneralLedgerCodeSelect from "./GeneralLedgerCodeSelect";

const FLATPICKR_FORMAT = "n/j/y at h:iK";

const MAX_CAPACITY = 2147483647;

const poolSelectValue = (product) =>
  product.series_pass_inventory_pool_id == null ? "" : String(product.series_pass_inventory_pool_id);

const generalLedgerCodeSelectValue = (product) =>
  product.general_ledger_code_id == null ? "" : String(product.general_ledger_code_id);

const toLocalDatetimeString = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const POPUP_STYLE = {
  border: "none",
  borderRadius: 8,
  padding: 0,
  width: "min(560px, calc(100vw - 40px))",
  maxHeight: "calc(100vh - 80px)",
  overflowY: "auto",
  textAlign: "left",
};

const toAmountInput = (value) => {
  if (value == null || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : String(value);
};

const toRow = (pricePoint) => ({
  uid: `saved-${pricePoint.id}`,
  id: pricePoint.id,
  name: pricePoint.name || "",
  price: toAmountInput(pricePoint.price),
  startsAt: pricePoint.starts_at ? String(pricePoint.starts_at).slice(0, 16) : "",
});

const EditPassProductModal = ({ product, onClose }) => {
  const { handleUpdatePassProduct, series } = useSeriesShowContext();
  const currency = series.base_currency_symbol;

  const availableTaxes = series.available_manual_taxes || [];

  const orphanedTaxes = ((product && product.taxable_items) || [])
    .filter((taxableItem) => !availableTaxes.some((tax) => tax.id === taxableItem.manual_tax_id))
    .map((taxableItem) => ({
      id: taxableItem.manual_tax_id,
      name: taxableItem.manual_tax_name
        ? `${taxableItem.manual_tax_name} (no longer offered at this venue)`
        : "Tax no longer offered at this venue",
      rate: taxableItem.manual_tax_rate === undefined ? null : taxableItem.manual_tax_rate,
    }));

  const taxRows = [...availableTaxes, ...orphanedTaxes];

  const newRowSeq = useRef(0);
  const [description, setDescription] = useState("");
  const [capacity, setCapacity] = useState("");
  const [inventoryPoolId, setInventoryPoolId] = useState("");
  const [generalLedgerCodeId, setGeneralLedgerCodeId] = useState("");
  const [basePrice, setBasePrice] = useState("");
  const [rows, setRows] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  const [selectedTaxIds, setSelectedTaxIds] = useState([]);
  const [deliverLater, setDeliverLater] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState("");
  const [announceChange, setAnnounceChange] = useState(true);
  const [roundUp, setRoundUp] = useState(false);
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const questions = useCheckoutQuestions();

  useEffect(() => {
    if (!product) return;
    questions.reset(product.custom_fields || []);
    setDescription(product.description || "");
    setCapacity(product.capacity == null ? "" : String(product.capacity));
    setInventoryPoolId(poolSelectValue(product));
    setGeneralLedgerCodeId(generalLedgerCodeSelectValue(product));
    setBasePrice(toAmountInput(product.base_price));
    setRows((product.price_points || []).map(toRow));
    setRemovedIds([]);
    setSelectedTaxIds((product.taxable_items || []).map((taxableItem) => taxableItem.manual_tax_id));

    const delayed = product.delivery_type === "delayed";
    setDeliverLater(delayed);
    setDeliveryTime(delayed && product.delivery_time ? String(product.delivery_time).slice(0, 16) : "");

    setAnnounceChange(product.show_upcoming_price_change ?? true);
    setRoundUp(product.round_up ?? false);
    setActive(product.active ?? true);
    setError(null);
    setSubmitting(false);
  }, [product?.id]);

  const activePricePointId = product && product.active_price_point ? product.active_price_point.id : null;

  const updateRow = (index, changes) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  };

  const addRow = () => {
    newRowSeq.current += 1;
    setRows((current) => [
      ...current,
      { uid: `new-${newRowSeq.current}`, id: null, name: "", price: "", startsAt: "" },
    ]);
  };

  const removeRow = (index) => {
    const row = rows[index];
    if (!row) return;

    if (row.id) setRemovedIds((ids) => [...ids, row.id]);
    setRows((current) => current.filter((_, i) => i !== index));
  };

  const toggleTax = (taxId) =>
    setSelectedTaxIds((current) =>
      current.includes(taxId) ? current.filter((id) => id !== taxId) : [...current, taxId],
    );

  const taxableItemsAttributes = () => {
    const existing = product.taxable_items || [];
    const existingTaxIds = existing.map((taxableItem) => taxableItem.manual_tax_id);
    return [
      ...selectedTaxIds
        .filter((taxId) => !existingTaxIds.includes(taxId))
        .map((taxId) => ({ manual_tax_id: taxId })),
      ...existing
        .filter((taxableItem) => !selectedTaxIds.includes(taxableItem.manual_tax_id))
        .map((taxableItem) => ({ id: taxableItem.id, _destroy: "true" })),
    ];
  };

  const soldCount = (product && product.sold_count) || 0;
  const redemptionAllotment = product && product.redemption_allotment;
  const capacityFloor = Math.max(soldCount, 1);
  const basePriceValid = basePrice !== "" && Number(basePrice) > 0;
  const rowsValid = rows.every((row) => row.price !== "" && Number(row.price) > 0 && row.startsAt !== "");
  const deliveryValid = !deliverLater || deliveryTime !== "";
  const capacityValid =
    /^\d+$/.test(capacity) && Number(capacity) >= capacityFloor && Number(capacity) <= MAX_CAPACITY;
  const capacityChanged = product ? capacity !== String(product.capacity) : false;
  const inventoryPoolChanged = product ? inventoryPoolId !== poolSelectValue(product) : false;
  const generalLedgerCodeChanged = product ? generalLedgerCodeId !== generalLedgerCodeSelectValue(product) : false;
  const isValid = basePriceValid && rowsValid && deliveryValid && capacityValid && questions.valid;

  const isSeasonPass = product ? product.product_kind === "season_pass" : false;
  const allotmentHint = () => {
    if (redemptionAllotment == null) return "Should not exceed the smallest show's inventory.";
    if (isSeasonPass) {
      return `Every pass claims a redemption on every show the moment it sells, and each show accepts ${redemptionAllotment}, so capacity above ${redemptionAllotment} will strand buyers.`;
    }
    return `Each show accepts ${redemptionAllotment} redemptions. Capacity can exceed that, since buyers pick their own shows, but a popular show can fill up.`;
  };

  const capacityHint = [
    soldCount > 0
      ? `${soldCount} sold so far, so this can't go below ${soldCount}. Raising it puts the extra back on sale.`
      : "Must be at least 1.",
    allotmentHint(),
  ].join(" ");

  const submit = async () => {
    if (submitting || !isValid) return;
    try {
      setSubmitting(true);
      setError(null);
      await handleUpdatePassProduct(product.id, {
        description: description.trim() || null,
        ...(capacityChanged ? { capacity: Number(capacity) } : {}),
        ...(inventoryPoolChanged ? { series_pass_inventory_pool_id: inventoryPoolId || null } : {}),
        ...(generalLedgerCodeChanged ? { general_ledger_code_id: generalLedgerCodeId || null } : {}),
        price: basePrice,
        price_points_attributes: [
          ...rows.map((row) => ({
            id: row.id || undefined,
            name: row.name,
            price: row.price,
            starts_at: offsetDateForServer(row.startsAt, series.venue_time_zone),
          })),
          ...removedIds.map((id) => ({ id, _destroy: "true" })),
        ],
        show_upcoming_price_change: announceChange,
        round_up: roundUp,
        delivery_type: deliverLater ? "delayed" : "immediate",
        delivery_time: deliverLater ? offsetDateForServer(deliveryTime, series.venue_time_zone) : null,
        active,
        taxable_items_attributes: taxableItemsAttributes(),
        custom_fields_attributes: questions.attributes(),
      });
      onClose();
    } catch (e) {
      const apiErrors = e && e.response && e.response.data && e.response.data.errors;
      setError(
        Array.isArray(apiErrors)
          ? apiErrors.join(", ")
          : apiErrors || "Could not save this product. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popup
      open={product !== null && product !== undefined}
      modal
      className="popup-modal"
      contentStyle={POPUP_STYLE}
      onClose={onClose}
      closeOnDocumentClick
    >
      <div className="popup-modal-container" data-testid="edit-product-modal">
        <div className="row m-0 series-confirm-header">
          <div className="col series-confirm-header-col">
            <p className="mb-0 series-confirm-title"><strong>Edit product</strong></p>
          </div>
          <div className="col-xs-auto d-flex align-items-center">
            <a href="#" className="text-muted series-confirm-close" aria-label="Close" onClick={(e) => { e.preventDefault(); onClose(); }}>
              <i className="fa fa-times" />
            </a>
          </div>
        </div>

        <div className="row m-0">
          <div className="col-12 series-confirm-body">
            <p className="text-muted pass-edit-subject">{product ? product.name : ""}</p>

            <h6 className="pass-edit-section">Details</h6>

            <div className="mb-3">
              <label className="form-label" htmlFor="pass-description"><strong>Description</strong></label>
              <textarea
                id="pass-description"
                className="form-control"
                data-testid="pass-description"
                aria-describedby="pass-description-help"
                rows={4}
                placeholder="What this pass gets them"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <small className="text-muted" id="pass-description-help" data-testid="pass-description-help">
                Shown to fans before they buy. Each new line shows as its own line, so you can list
                what&apos;s included.
              </small>
            </div>

            <h6 className="pass-edit-section">Pricing</h6>

            <div className="mb-3">
              <label className="form-label" htmlFor="pass-base-price"><strong>Base price</strong></label>
              <div className="input-group">
                <span className="input-group-text">{fmtCurrencySymbol(currency)}</span>
                <input
                  id="pass-base-price"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  className="form-control"
                  data-testid="pass-base-price"
                  aria-describedby="pass-base-price-help"
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="0.00"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                />
              </div>
              <small className="text-muted" id="pass-base-price-help" data-testid="pass-base-price-help">
                Applies until the first scheduled change starts.
                {product && product.active_price_point && (
                  <strong>
                    {` A scheduled change is already in effect — this pass currently sells for ${fmtPrice(product.price, currency)}. Edit the change marked "In effect now" to change that.`}
                  </strong>
                )}
              </small>
            </div>

            <div className="mb-3" data-testid="pass-price-points">
              <label className="form-label"><strong>Scheduled price changes</strong></label>
              {rows.length === 0 && (
                <p className="text-muted pass-edit-hint" data-testid="pass-price-points-empty">
                  No price changes scheduled. Add one to run early-bird pricing that steps up on a date.
                </p>
              )}

              {rows.map((row, index) => (
                <div className="pass-price-change" key={row.uid} data-testid={`price-point-row-${index}`}>
                  <div className="pass-price-change__head">
                    <span className="pass-price-change__title">
                      {`Change ${index + 1}`}
                      {row.id && activePricePointId === row.id && (
                        <span
                          className="series-status-pill series-status-live pass-price-change__live"
                          data-testid={`price-point-active-${index}`}
                        >
                          In effect now
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="pass-price-change__remove"
                      aria-label={`Remove price change ${index + 1}`}
                      data-testid={`remove-price-point-${index}`}
                      onClick={() => removeRow(index)}
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>

                  <div className="mb-2">
                    <label className="form-label" htmlFor={`price-point-name-${index}`}>Label</label>
                    <input
                      id={`price-point-name-${index}`}
                      type="text"
                      className="form-control"
                      data-testid={`price-point-name-${index}`}
                      placeholder="Regular, Day of show..."
                      value={row.name}
                      onChange={(e) => updateRow(index, { name: e.target.value })}
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label" htmlFor={`price-point-starts-at-${index}`}>Starts</label>
                    <Flatpickr
                      id={`price-point-starts-at-${index}`}
                      className="form-control"
                      data-testid={`price-point-starts-at-${index}`}
                      aria-label={`Price change ${index + 1} starts`}
                      placeholder="Select date / time"
                      options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT, defaultHour: 0, defaultMinute: 0 }}
                      value={row.startsAt ? new Date(row.startsAt) : ""}
                      onClose={([date]) => updateRow(index, { startsAt: toLocalDatetimeString(date) })}
                    />
                  </div>

                  <div>
                    <label className="form-label" htmlFor={`price-point-price-${index}`}>Price</label>
                    <div className="input-group">
                      <span className="input-group-text">{fmtCurrencySymbol(currency)}</span>
                      <input
                        id={`price-point-price-${index}`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        className="form-control"
                        data-testid={`price-point-price-${index}`}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        value={row.price}
                        onChange={(e) => updateRow(index, { price: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button variant="tertiary" size="sm" icon={Plus} data-testid="add-price-point" onClick={addRow}>
                Add a price change
              </Button>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="edit-announce-price-change"
                  data-testid="edit-announce-price-change"
                  aria-describedby="edit-announce-price-change-help"
                  checked={announceChange}
                  onChange={() => setAnnounceChange((current) => !current)}
                />
                <label className="form-check-label" htmlFor="edit-announce-price-change">
                  Tell fans about the next price change
                </label>
              </div>
              <small
                className="text-muted"
                id="edit-announce-price-change-help"
                data-testid="edit-announce-price-change-help"
              >
                {announceChange
                  ? "The purchase page shows the new price and the date it starts, so buyers know the deadline."
                  : "Fans see only the current price. Scheduled changes still take effect on time."}
              </small>
            </div>

            {taxRows.length > 0 && (
              <>
                <h6 className="pass-edit-section">Taxes</h6>

                <div className="mb-3" data-testid="edit-pass-taxes">
                  {taxRows.map((tax) => (
                    <div className="form-check" key={tax.id}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`edit-pass-tax-${tax.id}`}
                        data-testid={`edit-pass-tax-${tax.id}`}
                        aria-describedby="edit-pass-taxes-help"
                        checked={selectedTaxIds.includes(tax.id)}
                        onChange={() => toggleTax(tax.id)}
                      />
                      <label className="form-check-label" htmlFor={`edit-pass-tax-${tax.id}`}>
                        {tax.rate === null ? tax.name : `${tax.name} (${parseFloat(tax.rate)}%)`}
                      </label>
                    </div>
                  ))}
                  <small className="text-muted" id="edit-pass-taxes-help" data-testid="edit-pass-taxes-help">
                    Applied on top of the price at checkout. Changes affect future sales only.
                  </small>
                </div>
              </>
            )}

            <h6 className="pass-edit-section">Fees</h6>

            <PassFeesSection
              idPrefix="edit-pass"
              enabled={Boolean(product)}
              price={basePrice}
              roundUp={roundUp}
              onRoundUpChange={setRoundUp}
              taxIds={selectedTaxIds}
            />

            <h6 className="pass-edit-section">Delivery</h6>

            <div className="mb-3" data-testid="edit-delivery">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="edit-deliver-later"
                  data-testid="edit-delivery-later"
                  aria-describedby="edit-delivery-help"
                  checked={deliverLater}
                  onChange={() => setDeliverLater((current) => !current)}
                />
                <label className="form-check-label" htmlFor="edit-deliver-later">
                  {isSeasonPass ? "Hold the barcode until a set date" : "Hold the redemption code until a set date"}
                </label>
              </div>
              {deliverLater ? (
                <div className="mt-2">
                  <Flatpickr
                    className="form-control"
                    data-testid="edit-delivery-time"
                    aria-label="Delivery date"
                    placeholder="Select date / time"
                    options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT, minDate: "today" }}
                    value={deliveryTime ? new Date(deliveryTime) : ""}
                    onClose={([date]) => setDeliveryTime(toLocalDatetimeString(date))}
                  />
                  <small className="text-muted" id="edit-delivery-help">
                    {isSeasonPass
                      ? "Buyers see “available on this date” until then. The pass still scans at the door, so you can admit someone early."
                      : "Buyers see “available on this date” instead of their redemption code until then. A signed-in buyer's pack still applies at checkout, so they can claim shows early."}
                  </small>
                </div>
              ) : (
                <small className="text-muted" id="edit-delivery-help">
                  {isSeasonPass
                    ? "The barcode is available to buyers as soon as they purchase."
                    : "The redemption code is available to buyers as soon as they purchase."}
                </small>
              )}
            </div>

            <h6 className="pass-edit-section">Checkout questions</h6>

            <CheckoutQuestionsEditor questions={questions} />

            <h6 className="pass-edit-section">Inventory</h6>

            <div className="mb-3">
              <label className="form-label" htmlFor="pass-capacity"><strong>Capacity</strong></label>
              <input
                id="pass-capacity"
                type="number"
                inputMode="numeric"
                min={capacityFloor}
                max={MAX_CAPACITY}
                step="1"
                className="form-control"
                data-testid="pass-capacity"
                aria-describedby="pass-capacity-help"
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="How many can be sold"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
              <small className="text-muted" id="pass-capacity-help" data-testid="pass-capacity-help">
                {capacityHint}
              </small>
            </div>

            <InventoryPoolSelect
              pools={series.series_pass_inventory_pools || []}
              value={inventoryPoolId}
              onChange={setInventoryPoolId}
              lockedReason={
                product && product.inventory_pool_locked
                  ? "This product has sales, so it stays in its pool. You can still change its capacity or the pool's."
                  : null
              }
              joinWarning={
                inventoryPoolChanged && soldCount > 0
                  ? `This product has already sold ${soldCount}, and they will count against the pool. Once it joins, it can't leave.`
                  : null
              }
            />

            <GeneralLedgerCodeSelect
              codes={series.available_general_ledger_codes}
              value={generalLedgerCodeId}
              onChange={setGeneralLedgerCodeId}
            />

            <h6 className="pass-edit-section">Status</h6>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="edit-active"
                  data-testid="edit-active"
                  aria-describedby="edit-active-help"
                  checked={active}
                  onChange={() => setActive((current) => !current)}
                />
                <label className="form-check-label" htmlFor="edit-active">
                  On sale
                </label>
              </div>
              <small className="text-muted" id="edit-active-help">
                {active
                  ? "Fans can buy this product on the public series page."
                  : "Hidden from the public series page. Passes already sold keep working."}
              </small>
            </div>

            {error && (
              <div className="alert alert-danger py-2" role="alert" data-testid="edit-product-error">{error}</div>
            )}

            <div className="d-flex justify-content-end series-confirm-actions">
              <Button variant="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                data-testid="edit-product-submit"
                onClick={submit}
                disabled={submitting || !isValid}
              >
                {submitting ? "Saving…" : "Save product"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

EditPassProductModal.propTypes = {
  product: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

EditPassProductModal.defaultProps = {
  product: null,
};

export default EditPassProductModal;
